import { Header } from '@Components/StickyTable/BPMTable';
import { HeatmapCell } from './AmcPathingHeatmapCell';
import {
  AdTypeFilterGroup,
  AdTypePathTableRow,
  CampaignAttributionTableRow,
  CampaignPathTableRow
} from './amc-pathing-analysis-types';

export const HEADER_HEIGHT = 30;

// Ad type identity colors, shared by the touchpoint pie charts' legend/slices — no design-system
// equivalent (fixed per-ad-type brand colors).
export const AD_TYPE_CHART_COLORS: Record<string, { label: string; fillColor: string }> = {
  olv: { label: 'OLV', fillColor: '#7cb342' },
  sd: { label: 'SD', fillColor: '#f66e01' },
  sb: { label: 'SB', fillColor: '#0072f0' },
  display: { label: 'Display', fillColor: '#f20196' },
  sp: { label: 'SP', fillColor: '#00b6cb' },
  stv: { label: 'STV', fillColor: '#a64d79' },
  pv: { label: 'PV', fillColor: '#ffa800' },
  st: { label: 'ST', fillColor: '#388e3c' },
  '': { label: 'Unknown', fillColor: '#000' }
};

export const FILTER_COLUMN_WIDTH = 300;

export const DEFAULT_AD_TYPE_FILTERS: AdTypeFilterGroup[] = [
  {
    label: 'Amazon DSP',
    columnWidth: FILTER_COLUMN_WIDTH,
    data: [
      { name: 'Display', label: 'Display', value: 'include' },
      { name: 'OLV', label: 'OLV', value: 'include' },
      { name: 'STV', label: 'STV', value: 'include' },
      { name: 'PV', label: 'Prime Video (PV)', value: 'include' }
    ]
  },
  {
    label: 'Sponsored Ads',
    columnWidth: FILTER_COLUMN_WIDTH,
    data: [
      { name: 'SP', label: 'Sponsored Products', value: 'include' },
      { name: 'SB', label: 'Sponsored Brand', value: 'include' },
      { name: 'SD', label: 'Sponsored Display', value: 'include' },
      { name: 'ST', label: 'Sponsored TV (ST)', value: 'include' }
    ]
  }
];

export const campaignPathTableHeaders: Header[] = [
  {
    name: 'path',
    label: 'Campaign Path',
    flex: 1,
    minFlexWidth: 300,
    renderer: (data: CampaignPathTableRow) => <HeatmapCell value={data.path} align='left' />
  },
  {
    name: 'reach',
    label: 'Reach',
    width: 130,
    renderer: (data: CampaignPathTableRow) => (
      <HeatmapCell value={data.reach} intensity={data.heatMapValue_reach} format='count' />
    )
  },
  {
    name: 'impressions',
    label: 'Impressions',
    width: 130,
    renderer: (data: CampaignPathTableRow) => (
      <HeatmapCell
        value={data.impressions}
        intensity={data.heatMapValue_impressions}
        format='count'
      />
    )
  },
  {
    name: 'totalCost',
    label: 'Spend',
    width: 130,
    renderer: (data: CampaignPathTableRow) => (
      <HeatmapCell value={data.totalCost} intensity={data.heatMapValue_totalCost} format='money' />
    )
  },
  {
    name: 'totalPurchases',
    label: 'Purchases',
    width: 130,
    renderer: (data: CampaignPathTableRow) => (
      <HeatmapCell
        value={data.totalPurchases}
        intensity={data.heatMapValue_totalPurchases}
        format='count'
      />
    )
  },
  {
    name: 'totalProductSales',
    label: 'Total Sales',
    width: 130,
    renderer: (data: CampaignPathTableRow) => (
      <HeatmapCell
        value={data.totalProductSales}
        intensity={data.heatMapValue_totalProductSales}
        format='money'
      />
    )
  }
];

export const campaignAttrTableTooltips: Record<string, string> = {
  'First Touch ROAS':
    'The First Touch model attributes the entire value of a sale or conversion to the first customer interaction with an ad within the lookback period.',
  'Last Touch ROAS':
    'The Last Touch model attributes the entire value of a sale or conversion to the last customer interaction with an ad prior to a conversion within the lookback period.',
  'Linear ROAS':
    'The Linear model distributes the credit for a sale evenly across all the ads the customer interacted with during their purchasing journey.',
  'Position ROAS':
    'The Position Based model, also known as the U-shaped model, allocates more credit to the first and last interactions (typically 40% each), with the remaining 20% spread out among other touch points.'
};

export const campaignAttrTableHeaders: Header[] = [
  {
    name: 'campaign',
    label: 'Campaign',
    flex: 1,
    minFlexWidth: 300,
    renderer: (data: CampaignAttributionTableRow) => (
      <HeatmapCell value={data.campaign} align='left' />
    )
  },
  {
    name: 'First Touch',
    label: 'First Touch ROAS',
    width: 160,
    renderer: (data: CampaignAttributionTableRow) => (
      <HeatmapCell value={data['First Touch']} format='money' align='right' />
    )
  },
  {
    name: 'Last Touch',
    label: 'Last Touch ROAS',
    width: 160,
    renderer: (data: CampaignAttributionTableRow) => (
      <HeatmapCell value={data['Last Touch']} format='money' align='right' />
    )
  },
  {
    name: 'Linear',
    label: 'Linear ROAS',
    width: 160,
    renderer: (data: CampaignAttributionTableRow) => (
      <HeatmapCell value={data.Linear} format='money' align='right' />
    )
  },
  {
    name: 'Position',
    label: 'Position ROAS',
    width: 160,
    renderer: (data: CampaignAttributionTableRow) => (
      <HeatmapCell value={data.Position} format='money' align='right' />
    )
  }
];

export const adTypePathTableHeaders: Header[] = [
  {
    name: 'path',
    label: 'Ad Path',
    flex: 1,
    minFlexWidth: 300,
    renderer: (data: AdTypePathTableRow) => <HeatmapCell value={data.path} align='left' />
  },
  {
    name: 'reach',
    label: 'Reach',
    width: 130,
    renderer: (data: AdTypePathTableRow) => (
      <HeatmapCell value={data.reach} intensity={data.heatMapValue_reach} format='count' />
    )
  },
  {
    name: 'impressions',
    label: 'Impressions',
    width: 130,
    renderer: (data: AdTypePathTableRow) => (
      <HeatmapCell
        value={data.impressions}
        intensity={data.heatMapValue_impressions}
        format='count'
      />
    )
  },
  {
    name: 'userThatPurchased',
    label: 'Purchaser',
    width: 130,
    renderer: (data: AdTypePathTableRow) => (
      <HeatmapCell
        value={data.userThatPurchased}
        intensity={data.heatMapValue_userThatPurchased}
        format='count'
      />
    )
  },
  {
    name: 'totalPurchases',
    label: 'Total Purchases',
    width: 130,
    renderer: (data: AdTypePathTableRow) => (
      <HeatmapCell
        value={data.totalPurchases}
        intensity={data.heatMapValue_totalPurchases}
        format='count'
      />
    )
  },
  {
    name: 'purchaseRate',
    label: 'Purchase Rate',
    width: 130,
    renderer: (data: AdTypePathTableRow) => (
      <HeatmapCell
        value={data.purchaseRate}
        intensity={data.heatMapValue_purchaseRate}
        format='percent'
      />
    )
  }
];
